import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  PAGE_META,
} from "@/lib/seo";

/**
 * Reusable per-page SEO component.
 * Reads PAGE_META by current pathname or accepts explicit props (used by
 * dynamic pages like /blog/:slug).
 */
export default function Seo({
  title,
  description,
  keywords,
  image,
  noindex,
  type = "website",
  jsonLd,
}) {
  const { pathname } = useLocation();
  const meta = PAGE_META[pathname] || {};

  const finalTitle = title || meta.title || SITE_NAME;
  const finalDesc = description || meta.description || SITE_DESCRIPTION;
  const finalKw = keywords || meta.keywords || "";
  const finalImg = image || DEFAULT_OG_IMAGE;
  const finalNoIndex = noindex ?? meta.noindex ?? false;
  const url = `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKw && <meta name="keywords" content={finalKw} />}
      <link rel="canonical" href={url} />
      {finalNoIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large" />
      )}
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImg} />
      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
